import EditIcon from "@mui/icons-material/Edit"
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { DropzoneArea } from "mui-file-dropzone"
import { useEffect, useState } from "react"
import simulatorApplicationService from "services/simulatorApplicationService"
import trainingModuleService from "services/trainingModuleService"
import { setTrainingModules, updateTrainingModule } from "slices/trainingModuleSlice"
import type { ISimulatorApplication } from "types/simulatorApplicationTypes"
import type { ITrainingModule, IUpdateTrainingModule } from "types/trainingModuleTypes"
import * as Yup from "yup"

type Props = {
  trainingModule: ITrainingModule
}

export function UpdateTrainingModuleDialog({ trainingModule }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [simulatorApplications, setSimulatorApplications] = useState<Array<ISimulatorApplication>>(
    [],
  )
  const dispatch = useAppDispatch()

  const currentSimulator = useAppSelector(
    (state) => state.simulatorapplication.currentSimulatorApplication,
  )

  const initialValues: IUpdateTrainingModule = {
    name: trainingModule.name,
    description: trainingModule.description,
    imageFile: undefined,
    simulatorId: trainingModule.simulatorApplicationId,
  }

  const updateTrainingModuleValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter module name")
      .max(64, "Maximum name length is 64 characters"),
    description: Yup.string()
      .required("Please enter module description")
      .max(256, "Maximum description length is 256 characters"),
  })

  useEffect(() => {
    simulatorApplicationService
      .getSimulatorApplications()
      .then((resp) => {
        setSimulatorApplications(resp)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [])

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: updateTrainingModuleValidationScheme,
    onSubmit: (values) => {
      if (currentSimulator === undefined || !values.simulatorId) {
        return
      }

      setIsLoading(true)

      const trainingModuleUpdate: IUpdateTrainingModule = {
        name: values.name,
        description: values.description,
        imageFile: values.imageFile,
        simulatorId: values.simulatorId,
      }

      trainingModuleService
        .updateTrainingModule(trainingModule.id, trainingModuleUpdate)
        .then((resp) => {
          dispatch(updateTrainingModule(resp))
        })
        .then(() =>
          trainingModuleService.getSimulatorApplicationsTrainingModules(currentSimulator.id),
        ) //need to refresh training modules here in case one was moved to another simulator
        .then((resp2) => {
          dispatch(setTrainingModules(resp2))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <MenuItem onClick={handleClickOpen}>
        <EditIcon sx={{ marginRight: 1 }} />
        Update
      </MenuItem>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Update Training Module</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="simulator-id">Simulator *</InputLabel>
                  <Select
                    id="simulatorId"
                    labelId="simulator-id"
                    label="Simulator *"
                    name="simulatorId"
                    value={formik.values.simulatorId}
                    onChange={formik.handleChange}
                    error={formik.touched.simulatorId && Boolean(formik.errors.simulatorId)}
                  >
                    {simulatorApplications.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="description"
                  label="Description"
                  type="description"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <DropzoneArea
                  onChange={(files) => {
                    formik.setFieldValue("imageFile", files[0])
                  }}
                  filesLimit={1}
                  fileObjects={formik.values.imageFile}
                  acceptedFiles={["image/jpeg", "image/png", "image/bmp"]}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Update
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
